"use client"
 

import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useSession } from "next-auth/react"
import { useRouter, useSearchParams } from "next/navigation"
import { useAdminStatus } from "@/hooks/useAdminStatus"
import { useSettings } from "@/lib/SettingsContext"
import { getReservationFee } from "@/lib/fees"
import { promptPushPermission } from "@/lib/push-client"
import { useToast } from "@/components/ui/toast/use-toast"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Star, Clock, MapPin, Users, MessageCircle, Car, Flag, Settings, Calendar, Zap, Snowflake, AirVent, Usb, ShieldCheck, User, CalendarClock, CalendarX, ChevronRightIcon, MapIcon } from "lucide-react"
import { CancelBadgeRow } from "@/components/CancelBadge"
import type { CancelBadgePayload } from "@/lib/badge-copy"
import { formatAverage, hasRatings, statsFromUserFields, ratingWindowEndsAt as computeRatingWindowEndsAt } from "@/lib/rating-copy"
import { RatingPrompt } from "@/components/RatingPrompt"
import { pendingDriverTargets, passengerRatingTarget, type RatingTarget } from "@/lib/rating-targets"
import { formatTrips } from "@/lib/trip-copy"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { VEHICLE_LABELS, VEHICLE_ICONS, genderPreferenceForRow } from "@/lib/vehicle-config"
import type { VehicleType, Gender, GenderPreference, SeatData } from "@/components/SeatMap/seat-map.types"
import { normalizeRowCaps, rowCapNotice, type RowCaps } from "@/lib/row-cap"
import dynamic from "next/dynamic"
import Image from "next/image"
import Link from "next/link"
import {
  getRoutePoints,
  segmentPriceForTrecho,
  computeTrechoAvailability,
  computeTrechoAvailabilityByKm,
  cumulativeKmAtRoutePoints,
  trechoPolylinePoints,
  trechoPolylinePointsFromKm,
  seatAvailableForTrecho,
  seatAvailableForKm,
  type RoutePoint,
  type SegmentReservation,
  trechosOverlap,
  trechoOverlapByKm,
} from "@/lib/segment-utils"
import { normalizeSeatPricing, seatPricesForTrecho, comfortStateFor, seatCategory, comfortSurcharge, type SeatPricingConfig } from "@/lib/seat-pricing"
import {
  trechoDepartureDate,
  trechoArrivalDate,
  trechoDepartureDateFromTime,
  trechoArrivalDateFromTime,
  trechoTimeAtKm,
  formatTime,
} from "@/lib/segment-times"
import { computeSegmentPrice, buildPathFromRide } from "@/lib/route-matching"
import { reservationMapPoints } from "@/lib/reservation-map-points"
import { firstName, ceilTimeTo5, formatDistanceKm } from "@/lib/utils"
import { isRideFinished } from "@/lib/trip-summary-rules"
import {
  buildRouteTimeline,
  shouldExpandRouteTimeline,
  durationBetweenTimes,
  formatDurationMinutes,
  type RouteTimelineData,
  type RouteTimelinePoint,
} from "@/lib/route-timeline"
import { splitPlaceName } from "@/lib/place-name"
import { PlaceName } from "@/components/PlaceName"
import { RouteTimeline } from "@/components/RouteTimeline"
import { RouteMapDialog } from "@/components/RouteMapDialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

// Chunks pesados carregados sob demanda (item 4.1d do PLANO_FOCADO_PEDILLO.md):
// mapa de assentos e SDK do MercadoPago saem do bundle principal. O mapa da
// rota em tela cheia ("Ver no mapa") é carregado sob demanda no RouteMapDialog.
const SeatMap = dynamic(
  () => import("@/components/SeatMap/SeatMap").then((m) => m.SeatMap),
  { ssr: false, loading: () => <Skeleton className="w-full min-h-[300px] rounded-xl" /> }
)
const MercadoPagoBricks = dynamic(
  () => import("@/components/MercadoPagoBricks").then((m) => m.MercadoPagoBricks),
  { ssr: false, loading: () => <Skeleton className="w-full min-h-[280px] rounded-xl" /> }
)

interface DriverData {
  id: string
  name: string
  avatar: string | null
  /** Média recebida COMO MOTORISTA; `null` = ainda sem avaliações. */
  ratingAsDriver: number | null
  ratingCountAsDriver: number
  /** Caronas já liquidadas em que a pessoa dirigiu. Renomeado de `trips`. */
  tripsAsDriver: number
  verified: boolean
  cancelBadge?: CancelBadgePayload | null
}

interface VehicleData {
  model: string
  color: string
  year: string
  brand: string
}

interface ReservationData {
  _id: string
  passengerId: string
  name: string
  avatar: string | null
  seatId: string
  status: string
  boardStopIndex: number
  alightStopIndex: number
  boardKm?: number | null
  alightKm?: number | null
  boardEstimatedAt?: string | null
  segment: string | null
  origin: string | null
  destination: string | null
  price?: number
}

interface SeatMapData {
  vehicleType: VehicleType
  seats: SeatData[]
  genderPolicy: any
  seatPricing?: SeatPricingConfig
  comfortState?: Record<string, unknown>
  /** Limite "até dois" por fileira (`rowKey → RowCap`). */
  rowCaps?: RowCaps
}

interface RideData {
  _id: string
  status?: string
  settledAt?: string | null
  origin: { name: string; placeId: string; lat: number; lng: number }
  destination: { name: string; placeId: string; lat: number; lng: number }
  stops: Array<{
    name: string
    placeId: string
    lat: number
    lng: number
    price: number
    order: number
    /** Parada de cidade (via `normalizeRideLocations`); é o que vira item da rota. */
    cityName?: string | null
  }>
  route: {
    polyline: string
    overviewPolyline: string
    sampledCoordinates: Array<{ lat: number; lng: number }>
    distance: string
    duration: string
  }
  date: string
  departureTime: string
  estimatedArrivalTime: string
  availableSeats: number
  frontSeat: boolean
  price: number
  description: string
  driverId: string
  vehicleType?: VehicleType
  seatMap?: SeatMapData
  driver?: DriverData
  vehicle?: VehicleData
  reservations?: ReservationData[]
  turboMode?: boolean
  autoAccept?: boolean
}

/**
 * Janela de trás pode optar por conforto neste lote? (banco do meio precisa
 * estar livre para o trecho e a fileira não pode estar quebrada). Exclui
 * janelas pendentes de decisão de OUTRO passageiro — em fila, quem não é o
 * optante só decide via diálogo SIM/NÃO, nunca pelo preço.
 */
function comfortEligible(
  ride: RideData | null,
  seatId: string,
  cfg: SeatPricingConfig,
  userId?: string,
  isSeatFree?: (seatId: string) => boolean
): boolean {
  const seats = ride?.seatMap?.seats ?? []
  const seatCfg = seats.find((s) => s.id === seatId)
  if (!seatCfg || seatCategory(seatCfg) !== "back_window") return false
  if (cfg.comfortBack !== true) return false
  // Sem o banco do MEIO livre para este trecho não há "conforto de sobra": se
  // alguém já senta no meio, a fileira está cheia de fato.
  if (isSeatFree) {
    const middle = seats.find((s) => s.row === seatCfg.row && seatCategory(s) === "middle")
    if (!middle || !isSeatFree(middle.id)) return false
  }
  const state = comfortStateFor(seats, ride, seatId)
  if (state.status === "pending" && !state.optedIn.includes(userId ?? "")) return false
  return true
}

export default function TripDetailsContent({ id, initialRide }: { id: string; initialRide?: RideData | null }) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { data: session } = useSession()
  const isAdmin = useAdminStatus()
  const { settings } = useSettings()
  const { toast } = useToast()
  const [ride, setRide] = useState<RideData | null>(initialRide ?? null)
  const [loading, setLoading] = useState(!initialRide)
  const [error, setError] = useState<string | null>(null)
  const [showPayment, setShowPayment] = useState(false)
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false)
  const [selectedSeatIds, setSelectedSeatIds] = useState<string[]>([])
  // Flags de conforto por assento selecionado: "opt" (quero conforto),
  // "confirm" (SIM à decisão pendente da outra janela) ou "decline" (NÃO).
  const [comfortFlags, setComfortFlags] = useState<Record<string, "opt" | "confirm" | "decline">>({})
  const [comfortPromptSeat, setComfortPromptSeat] = useState<string | null>(null)
  // Decisão de conforto da janela B já reservada: aceitar (abre pagamento do
  // adicional via `comfort_confirm`) ou recusar (libera o meio).
  const [showComfortPayment, setShowComfortPayment] = useState(false)
  const [comfortBusy, setComfortBusy] = useState(false)
  const [reserving, setReserving] = useState(false)
  const [reserveError, setReserveError] = useState("")
  const [genderNotice, setGenderNotice] = useState("")
  // Condicional de gênero por proximidade: quando marcado, cada assento de trás
  // selecionado leva `genderAdjacencyGuard` no payload — se uma pessoa de outro
  // gênero reservar o assento ao lado no mesmo trecho, a reserva é cancelada e
  // reembolsada automaticamente.
  const [genderGuard, setGenderGuard] = useState(false)
  const [boardStopIndex, setBoardStopIndex] = useState(0)
  const [alightStopIndex, setAlightStopIndex] = useState(2)
  // Mapa da rota em tela cheia ("Ver no mapa"). Vive fora do card embutido
  // porque aquele só monta quando a busca casou a carona fora da rota.
  const [showRouteMap, setShowRouteMap] = useState(false)
  const [ratedTargetIds, setRatedTargetIds] = useState<Set<string>>(new Set())
  const [ratingEligibilityLoaded, setRatingEligibilityLoaded] = useState(false)
  const [nowTick] = useState(() => Date.now())
  // Guarda de "intenção já consumida" para a retomada pós-login: o token só
  // pode ser consumido UMA vez, e o efeito re-executa quando os searchParams
  // mudam (o próprio strip do token) ou quando session/ride/settings resolvem.
  const intentConsumedRef = useRef(false)
// console.log("ride", ride)
  // console.log("ride.seatMap.genderPolicy", ride?.seatMap?.genderPolicy)
  const isLoggedIn = !!session?.user?.id
  const ratingWindowEndsAt = useMemo(() => computeRatingWindowEndsAt(ride), [ride])
  const ratingWindowExpired = useMemo(
    () => (ratingWindowEndsAt ? nowTick > ratingWindowEndsAt.getTime() : false),
    [ratingWindowEndsAt, nowTick]
  )
  const confirmedPassengers = useMemo(
    () => ride?.reservations?.filter((r) => r.status === "confirmed" || r.status === "pending") || [],
    [ride]
  )

  // `average: null` = sem avaliações, e aí a estrela não aparece. O código
  // anterior era `ride.driver?.rating || 0` com a estrela incondicional, o que
  // imprimia "0.0" para todo motorista novo — leitura de avaliação péssima
  // onde o certo é não mostrar nada.
  const driverRating = useMemo(
    () => statsFromUserFields(ride?.driver?.ratingAsDriver, ride?.driver?.ratingCountAsDriver),
    [ride?.driver?.ratingAsDriver, ride?.driver?.ratingCountAsDriver]
  )
  const driverTrips = ride?.driver?.tripsAsDriver ?? 0
  // QUEM este usuário pode avaliar NESTA carona. A regra mora em
  // `lib/rating-targets.ts` (puro e testado); aqui é só o desenho. O papel
  // vem do dono da carona: o motorista avalia passageiro CONFIRMADO e o
  // passageiro avalia o motorista.
  const ratingPrompt = useMemo(() => {
    if (!ride || !isLoggedIn || !session?.user?.id) {
      return { role: "passenger" as const, targets: [] as RatingTarget[] }
    }
    if (String(ride.driverId) === String(session.user.id)) {
      return {
        role: "driver" as const,
        targets: pendingDriverTargets(ride.reservations, {
          ratedTargetIds,
          selfId: session.user.id,
        }),
      }
    }
    const myReservation =
      ride.reservations?.find((r) => r.passengerId === session.user.id && r.status === "confirmed") || null
    const target = passengerRatingTarget({
      driver: ride.driver,
      myReservation,
      ratedTargetIds,
      fallbackLabel:
        ride.origin?.name && ride.destination?.name ? `${ride.origin.name} → ${ride.destination.name}` : null,
    })
    return { role: "passenger" as const, targets: target ? [target] : [] }
  }, [ride, isLoggedIn, session, ratedTargetIds])

  useEffect(() => {
    if (!ride?.settledAt || ratingWindowExpired || !isLoggedIn || !ride?._id) {
      queueMicrotask(() => setRatingEligibilityLoaded(true))
      return
    }
    let cancelled = false
    fetch(`/api/ratings?rideId=${ride._id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return
        queueMicrotask(() => setRatedTargetIds(new Set((data.ratedTargetIds || []).map(String))))
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) queueMicrotask(() => setRatingEligibilityLoaded(true))
      })
    return () => {
      cancelled = true
    }
  }, [ride?._id, ride?.settledAt, ratingWindowExpired, isLoggedIn])
  const isDriver = isLoggedIn && !!ride && ride.driverId === session?.user?.id
  const userGender = session?.user?.gender as Gender | undefined

  const queryBoard = Number(searchParams.get("boardStopIndex"))
  const queryAlight = Number(searchParams.get("alightStopIndex"))
  const fromSearch =
    Number.isInteger(queryBoard) && Number.isInteger(queryAlight) && queryBoard >= 0 && queryAlight > queryBoard
  const searchOriginName = (searchParams.get("originName") || "").trim()
  const searchDestName = (searchParams.get("destName") || "").trim()
  // Cidades da busca ("Medianeira, PR") — linha 1 (cidade) e título/OG.
  const searchOriginCityName = (searchParams.get("originCityName") || "").trim()
  const searchDestCityName = (searchParams.get("destCityName") || "").trim()
  const querySegmentPrice = Number(searchParams.get("segmentPrice"))
  const queryTrechoDepartureTime = searchParams.get("trechoDepartureTime")
  const queryTrechoArrivalTime = searchParams.get("trechoArrivalTime")
  // Novos params para visualização de deslocamento
  const searchOriginLat = Number(searchParams.get("originLat"))
  const searchOriginLng = Number(searchParams.get("originLng"))
  const searchDestLat = Number(searchParams.get("destLat"))
  const searchDestLng = Number(searchParams.get("destLng"))
  const matchOriginDistance = Number(searchParams.get("matchOriginDistance"))
  const matchDestDistance = Number(searchParams.get("matchDestDistance"))
  // Params contínuos (turbo): alongKm projetado + ponto de embarque/desembarque na rota
  const queryBoardKm = Number(searchParams.get("boardAlongKm"))
  const queryAlightKm = Number(searchParams.get("alightAlongKm"))
  const queryBoardLat = Number(searchParams.get("boardLat"))
  const queryBoardLng = Number(searchParams.get("boardLng"))
  const queryAlightLat = Number(searchParams.get("alightLat"))
  const queryAlightLng = Number(searchParams.get("alightLng"))

  // Flags para mostrar linhas tracejadas de deslocamento. Vêm antes dos
  // early-returns porque alimentam a decisão de expandir o trilho de rota,
  // que é um useMemo (hooks não podem ficar depois de `return` condicional).
  const ON_ROUTE_THRESHOLD_KM = 1
  const showOriginDisplacement = fromSearch &&
    Number.isFinite(matchOriginDistance) &&
    matchOriginDistance > ON_ROUTE_THRESHOLD_KM &&
    Number.isFinite(searchOriginLat) &&
    Number.isFinite(searchOriginLng)
  const showDestDisplacement = fromSearch &&
    Number.isFinite(matchDestDistance) &&
    matchDestDistance > ON_ROUTE_THRESHOLD_KM &&
    Number.isFinite(searchDestLat) &&
    Number.isFinite(searchDestLng)

  const continuousMode =
    fromSearch &&
    !!ride?.turboMode &&
    Number.isFinite(queryBoardKm) &&
    Number.isFinite(queryAlightKm) &&
    queryBoardKm >= 0 &&
    queryAlightKm > queryBoardKm &&
    Number.isFinite(queryBoardLat) &&
    Number.isFinite(queryBoardLng) &&
    Number.isFinite(queryAlightLat) &&
    Number.isFinite(queryAlightLng)

  // URL da carona pura (/trip/<id> sem params de trecho) não tem trecho a
  // destacar: o mapa cheio ("Ver no mapa") mostra só a rota da carona. Traçado
  // azul do trecho e pins de embarque/desembarque valem apenas quando a URL
  // veio da busca (paradas) ou do modo contínuo (turbo) — senão o "trecho"
  // seria a rota inteira desenhada por cima dela mesma.
  const trechoSelected = fromSearch || continuousMode

  // Passageiros do card: sem seleção de trecho na URL valem todos; com
  // trecho (paradas ou contínuo/turbo) só quem sobrepõe aquele trecho.
  //
  // Extraído para reuso: o seat map usa a MESMA regra (ver `seatPassengers`)
  // — um marcador de avatar só aparece se a reserva cruza o trecho consultado,
  // senão o passageiro de outro trecho "vaza" para o assento errado.
  const reservationOverlapsTrecho = useCallback(
    (r: ReservationData) => {
      if (continuousMode) {
        if (typeof r.boardKm !== "number" || typeof r.alightKm !== "number") return false
        if (!Number.isFinite(queryBoardKm) || !Number.isFinite(queryAlightKm)) return false
        return trechoOverlapByKm(queryBoardKm, queryAlightKm, r.boardKm, r.alightKm)
      }
      const rb = r.boardStopIndex
      const ra = r.alightStopIndex
      if (!Number.isInteger(rb) || !Number.isInteger(ra)) return false
      return trechosOverlap(rb, ra, queryBoard, queryAlight)
    },
    [continuousMode, queryBoardKm, queryAlightKm, queryBoard, queryAlight]
  )

  const passengersInSelectedSegment = useMemo(() => {
    if (!fromSearch && !continuousMode) return confirmedPassengers
    return confirmedPassengers.filter(reservationOverlapsTrecho)
  }, [confirmedPassengers, fromSearch, continuousMode, reservationOverlapsTrecho])

  const groupedPassengersToShow = useMemo(() => {
    const map = new Map<string, { passengerId: string; name: string; avatar?: string | null; count: number; trechos: string[] }>()
    for (const r of passengersInSelectedSegment) {
      const key = r.passengerId || r.name || "?"
      const trecho = r.origin && r.destination ? `${r.origin.split(", ")[0]} → ${r.destination.split(", ")[0]}` : null
      const existing = map.get(key)
      if (existing) {
        existing.count += 1
        if (trecho && !existing.trechos.includes(trecho)) existing.trechos.push(trecho)
      } else {
        map.set(key, { passengerId: r.passengerId, name: r.name, avatar: r.avatar, count: 1, trechos: trecho ? [trecho] : [] })
      }
    }
    return Array.from(map.values())
  }, [passengersInSelectedSegment])

  const outsiderChatHref = useMemo(() => {
    if (!ride) return "#"
    const qs = new URLSearchParams(searchParams.toString())
    qs.set("target", String(ride.driverId))
    let seg = "b0:a1"
    if (fromSearch) seg = `b${queryBoard}:a${queryAlight}`
    else if (continuousMode) seg = `b${Math.round(queryBoardKm)}:a${Math.round(queryAlightKm)}`
    return `/chat/${ride._id}/${seg}?${qs.toString()}`
  }, [ride, searchParams, fromSearch, continuousMode, queryBoard, queryAlight, queryBoardKm, queryAlightKm])

  const loadRide = useCallback(async () => {
    try {
      const res = await fetch(`/api/rides/${id}`)
      if (!res.ok) {
        setError("Nosso estagiário está enfrentando dificuldades para carregar as informações da viagem")
        return
      }
      const data = await res.json()
      setRide(data)
      if (fromSearch) {
        const pointCount = (data.stops?.length || 0) + 2
        const maxBoard = Math.max(0, pointCount - 2)
        const maxAlight = Math.max(1, pointCount - 1)
        const board = Math.min(queryBoard, maxBoard)
        setBoardStopIndex(board)
        setAlightStopIndex(Math.max(board + 1, Math.min(queryAlight, maxAlight)))
      } else if (data.stops?.length) {
        setAlightStopIndex(data.stops.length + 1)
      }
    } catch {
      setError("Nosso estagiário está enfrentando dificuldades para carregar as informações da viagem")
    } finally {
      setLoading(false)
    }
  }, [id, fromSearch, queryBoard, queryAlight])

  useEffect(() => {
    queueMicrotask(() => loadRide())
  }, [loadRide])

  const routePoints = useMemo<RoutePoint[]>(() => (ride ? getRoutePoints(ride) : []), [ride])

  const segmentReservations = useMemo<SegmentReservation[]>(
    () =>
      (ride?.reservations || [])
        .filter((r) => r.seatId)
        .map((r) => ({
          reservationId: String(r._id),
          seatId: r.seatId,
          status: r.status,
          boardStopIndex: r.boardStopIndex,
          alightStopIndex: r.alightStopIndex,
          boardKm: r.boardKm ?? undefined,
          alightKm: r.alightKm ?? undefined,
        })),
    [ride]
  )

  const availability = useMemo(() => {
    if (!ride?.seatMap) return null
    if (continuousMode) {
      return computeTrechoAvailabilityByKm(
        ride.seatMap.seats,
        segmentReservations,
        queryBoardKm,
        queryAlightKm,
        cumulativeKmAtRoutePoints(ride, routePoints)
      )
    }
    return computeTrechoAvailability(ride.seatMap.seats, segmentReservations, boardStopIndex, alightStopIndex)
  }, [ride, segmentReservations, boardStopIndex, alightStopIndex, continuousMode, queryBoardKm, queryAlightKm, routePoints])

  const trechoDeparture = ride
    ? fromSearch
      ? trechoDepartureDateFromTime(ride, queryTrechoDepartureTime) ||
        (continuousMode ? trechoTimeAtKm(ride, queryBoardKm) : trechoDepartureDate(ride, boardStopIndex))
      : trechoDepartureDate(ride, boardStopIndex)
    : null
  const trechoArrival = ride
    ? continuousMode
      ? trechoArrivalDateFromTime(ride, queryTrechoArrivalTime) || trechoTimeAtKm(ride, queryAlightKm)
      : trechoArrivalDate(ride, alightStopIndex)
    : null
  const [now] = useState(() => Date.now())
  const departed = !!trechoDeparture && trechoDeparture.getTime() <= now

  const displaySeats = useMemo(() => {
    if (!ride?.seatMap?.seats || !availability) return ride?.seatMap?.seats || []
    return ride.seatMap.seats.map((s) => ({
      ...s,
      available: availability.availableSeatIds.includes(s.id),
    }))
  }, [ride, availability])
  // Passageiros marcados no seat map. Sem trecho na URL mostra a carona toda;
  // com trecho, só quem compartilha o trecho consultado — o passageiro vê os
  // colegas de trecho, o motorista vê quem está a bordo naquele trecho.
  const seatPassengers = useMemo(() => {
    const map: Record<
      string,
      { passengerId: string; name: string; avatar: string | null; status: string; reservationId: string; departed?: boolean }[]
    > = {}
    const reservations =
      fromSearch || continuousMode
        ? (ride?.reservations || []).filter(reservationOverlapsTrecho)
        : ride?.reservations || []
    for (const r of reservations) {
      if (!r.seatId || (r.status !== "pending" && r.status !== "confirmed")) continue
      const entry = {
        passengerId: r.passengerId,
        name: r.name,
        avatar: r.avatar,
        status: r.status,
        reservationId: String(r._id),
        departed: !!r.boardEstimatedAt && new Date(r.boardEstimatedAt).getTime() <= now,
      }
      if (!map[r.seatId]) map[r.seatId] = []
      map[r.seatId].push(entry)
    }
    return map
  }, [ride, now, fromSearch, continuousMode, reservationOverlapsTrecho])

  const segmentPrice = fromSearch && Number.isFinite(querySegmentPrice) && querySegmentPrice > 0
    ? querySegmentPrice
    : continuousMode && ride
      ? Math.round(computeSegmentPrice(ride, queryBoardKm, queryAlightKm, buildPathFromRide(ride)))
      : ride
        ? segmentPriceForTrecho(ride, boardStopIndex, alightStopIndex)
        : 0

  const trechoPoints = useMemo(() => {
    if (!ride || !trechoSelected || routePoints.length < 2) return []
    if (continuousMode) return trechoPolylinePointsFromKm(ride, queryBoardKm, queryAlightKm)
    const alongKm = cumulativeKmAtRoutePoints(ride, routePoints)
    return trechoPolylinePoints(ride, routePoints, alongKm, boardStopIndex, alightStopIndex)
  }, [ride, routePoints, boardStopIndex, alightStopIndex, continuousMode, trechoSelected, queryBoardKm, queryAlightKm])

  // O trilho é desenhado em torno do trecho consultado (o que a busca mostrou),
  // não da rota inteira da carona. Saiu da busca, o passageiro quer ver Foz →
  // Cascavel; as paradas de passagem fora dos dois endpoints ficam atrás de um
  // "… ver N paradas". Sem trecho na URL (dono/motorista abrindo a carona), vale
  // a rota inteira e o colapso não é forçado.
  const timelineBoardIndex = boardStopIndex
  const timelineAlightIndex = alightStopIndex

  // Trilho de rota: a listagem passa a ser a rota inteira quando a carona
  // tem cidades de passagem ou quando a origem/destino do passageiro saiu do
  // traçado. Sem esses dois casos, vale o trilho curto de dois pontos.
  const timeline = useMemo<RouteTimelineData>(
    () =>
      ride
        ? buildRouteTimeline(ride, {
            boardIndex: timelineBoardIndex,
            alightIndex: timelineAlightIndex,
            continuousMode,
            boardKm: continuousMode ? queryBoardKm : undefined,
            alightKm: continuousMode ? queryAlightKm : undefined,
            boardName: fromSearch ? searchOriginName : undefined,
            alightName: fromSearch ? searchDestName : undefined,
            // Cidades da busca: a linha sintética de embarque/desembarque
            // mostra a cidade em destaque + endereço completo.
            boardCityName: fromSearch ? searchOriginCityName || undefined : undefined,
            alightCityName: fromSearch ? searchDestCityName || undefined : undefined,
            boardTime: searchParams.get("trechoDepartureTime"),
            alightTime: searchParams.get("trechoArrivalTime"),
            // Vindo da busca, o trilho é o trecho: paradas de passagem (sem
            // embarque/desembarque) ficam atrás do "… ver N paradas".
            collapseIntermediate: fromSearch,
            reservations: (ride.reservations || [])
              .filter((r) => r.status === "confirmed" || r.status === "pending" || r.status === "awaiting_payment")
              .map((r) => ({
                origin: r.origin,
                destination: r.destination,
                name: r.name,
                avatar: r.avatar,
                boardStopIndex: r.boardStopIndex,
                alightStopIndex: r.alightStopIndex,
                boardKm: r.boardKm,
                alightKm: r.alightKm,
              })),
          })
        : { points: [], durationMinutes: null, trechoDurationMinutes: null },
    [
      ride,
      timelineBoardIndex,
      timelineAlightIndex,
      continuousMode,
      queryBoardKm,
      queryAlightKm,
      fromSearch,
      searchOriginName,
      searchDestName,
      searchOriginCityName,
      searchDestCityName,
      searchParams,
    ]
  )
  // Sem dois pontos a listagem completa não tem o que listar: cai no trilho
  // curto em vez de renderizar só o rótulo de duração solto na tela.
  const cityStopCount = ride?.stops?.filter((stop) => !!stop.cityName).length ?? 0
  const expandRouteTimeline =
    shouldExpandRouteTimeline({
      originDisplacement: showOriginDisplacement,
      destDisplacement: showDestDisplacement,
      cityStopCount,
    }) && timeline.points.length >= 2

  // O mapa plotado em tela cheia ("Ver no mapa") abre o RouteMapDialog. O card
  // de mapa embutido foi removido — a rota se vê no trilho. O botão aparece
  // sempre que houver polyline, independentemente de haver assentos vagos.
  const showRouteMapButton = !!ride.route?.overviewPolyline

  const mediation = !!settings.paymentMediation

  const pricingCfg = useMemo(() => normalizeSeatPricing(ride?.seatMap?.seatPricing), [ride?.seatMap?.seatPricing])

  // Aviso de fileira limitada ("até 2 pessoas") — o passageiro precisa saber
  // que a fileira não comporta 3, mesmo com o banco do meio aparecendo
  // bloqueado no mapa.
  const rowCapNoticeText = rowCapNotice(normalizeRowCaps(ride?.seatMap?.rowCaps))

  // Along em km de cada ponto da rota — usado pelo modo contínuo (turbo) para
  // checar disponibilidade do banco por faixa de km em vez de por índice de parada.
  const trechoAlongKm = useMemo(
    () => (ride ? cumulativeKmAtRoutePoints(ride, routePoints) : []),
    [ride, routePoints]
  )

  // Paradas de cidade que caem DENTRO do trecho consultado. No mapa do trecho
  // elas são só informativas (o embarque/desembarque é que importa), então vão
  // esmaecidas para não competir com o traço azul do trecho. O índice é no array
  // `ride.stops` (ordem original) — o mesmo passado ao mapa. A posição da parada
  // em `routePoints` casa por `order` (getRoutePoints ordena por ele).
  const dimStopIndexes = useMemo(() => {
    if (!ride || (!fromSearch && !continuousMode) || routePoints.length < 2) return []
    const stops = Array.isArray(ride.stops) ? ride.stops : []
    if (stops.length === 0) return []
    // Km do embarque/desembarque ao longo da rota. No modo contínuo o índice de
    // parada já foi resolvido a partir do km (linha ~444); nos demais, o índice
    // é o próprio trecho consultado.
    const boardKm = continuousMode ? queryBoardKm : trechoAlongKm[boardStopIndex]
    const alightKm = continuousMode ? queryAlightKm : trechoAlongKm[alightStopIndex]
    if (!Number.isFinite(boardKm) || !Number.isFinite(alightKm)) return []
    const lowKm = Math.min(boardKm, alightKm)
    const highKm = Math.max(boardKm, alightKm)
    // Km de cada parada na ordem de `routePoints` (origem, stops por order,
    // destino) — ignora os pontos que não são parada.
    const stopKms = routePoints
      .map((p, i) => ({ kind: p.kind, km: trechoAlongKm[i] ?? 0 }))
      .filter((x) => x.kind === "stop")
      .map((x) => x.km)
    // Índices de `ride.stops` na ordem de `order` (posição k ↔ a k-ésima stop).
    const byOrder = stops.map((_, i) => i).sort((a, b) => (stops[a].order ?? 0) - (stops[b].order ?? 0))
    return byOrder.filter((_, k) => {
      const km = stopKms[k]
      return typeof km === "number" && km > lowKm && km < highKm
    })
  }, [
    ride,
    fromSearch,
    continuousMode,
    routePoints,
    trechoAlongKm,
    boardStopIndex,
    alightStopIndex,
    queryBoardKm,
    queryAlightKm,
  ])

  // Pontos de embarque/desembarque das reservas ativas que NÃO são origem,
  // destino nem parada da carona (ex.: reserva Foz → Medianeira numa carona
  // cuja única parada é Cascavel — modo turbo permite descer em qualquer ponto
  // do traçado). São pinos próprios no mapa cheio, com o nome curto da cidade.
  // Canceladas ficam de fora: quem desistiu não deixa marca na rota.
  const reservationPoints = useMemo(() => {
    if (!ride) return []
    const active = (ride.reservations || []).filter(
      (r) => r.status === "confirmed" || r.status === "pending" || r.status === "awaiting_payment"
    )
    if (active.length === 0) return []
    const anchors = [
      { lat: ride.origin.lat, lng: ride.origin.lng },
      { lat: ride.destination.lat, lng: ride.destination.lng },
      ...(ride.stops || []).map((s) => ({ lat: s.lat, lng: s.lng })),
    ]
    return reservationMapPoints(active, routePoints, buildPathFromRide(ride), anchors)
  }, [ride, routePoints])

  // Mesma pergunta de `computeReservationAmount` (lib/payments.ts:99): há gente
  // sentada neste banco para ESTE trecho? `baseAvailable=true` de propósito —
  // assento desativado pelo motorista não gera desconforto e não dá desconto.
  const isSeatFreeInTrecho = useCallback(
    (seatId: string) =>
      continuousMode
        ? seatAvailableForKm(seatId, segmentReservations, queryBoardKm, queryAlightKm, trechoAlongKm, true)
        : seatAvailableForTrecho(seatId, segmentReservations, boardStopIndex, alightStopIndex, true),
    [continuousMode, segmentReservations, queryBoardKm, queryAlightKm, trechoAlongKm, boardStopIndex, alightStopIndex]
  )

  // Janelas de trás selecionadas que ainda PODEM optar por conforto neste lote
  // (banco do meio livre para este trecho).
  const comfortEligibleSeats = useMemo(
    () =>
      selectedSeatIds.filter((seatId) =>
        comfortEligible(ride, seatId, pricingCfg, session?.user?.id, isSeatFreeInTrecho)
      ),
    [selectedSeatIds, ride, pricingCfg, session?.user?.id, isSeatFreeInTrecho]
  )

  // Janelas desta fileira que já optaram por conforto neste lote (checkbox marcado)
  const optedNow = useMemo(
    () =>
      comfortEligibleSeats.filter(
        (sid) => comfortFlags[sid] === "opt" || comfortFlags[sid] === "confirm"
      ),
    [comfortEligibleSeats, comfortFlags]
  )

  // Adicional por janela exibido nas mensagens de conforto (R2: optIn e confirm pagam)
  const comfortSurge = useMemo(() => comfortSurcharge(segmentPrice), [segmentPrice])

  // Pedido de conforto PENDENTE endereçado ao próprio usuário: ele tem reserva
  // numa janela traseira em fileira `pending` mas NÃO é quem pediu — ou seja, é
  // a janela B, que precisa aceitar (pagar o adicional) ou recusar.
  const myPendingComfort = useMemo(() => {
    const uid = session?.user?.id
    if (!ride || !uid || isDriver) return null
    const seats = ride.seatMap?.seats ?? []
    if (seats.length === 0) return null
    const mine = (ride.reservations ?? []).filter(
      (r) =>
        r.passengerId === uid &&
        (r.status === "confirmed" || r.status === "pending")
    )
    for (const res of mine) {
      const seatCfg = seats.find((s) => s.id === res.seatId)
      if (!seatCfg || seatCategory(seatCfg) !== "back_window") continue
      const state = comfortStateFor(seats, ride, res.seatId)
      if (state.status !== "pending") continue
      if (state.optedIn.includes(uid)) continue
      return res
    }
    return null
  }, [ride, session?.user?.id, isDriver])

  // Adicional efetivamente cobrado da janela B: deriva do preço do PRÓPRIO
  // trecho reservado (o backend cobra `comfortSurcharge(reservation.price)`),
  // não do trecho que está sendo visualizado agora.
  const comfortDueAmount =
    myPendingComfort && myPendingComfort.price && myPendingComfort.price > 0
      ? comfortSurcharge(myPendingComfort.price)
      : comfortSurge

  // Assentos TRASEIROS selecionados aptos ao condicional de gênero (a dianteira
  // é fora do guard: `front_right` segue só a política de fileira do motorista).
  const guardEligibleSeats = useMemo(
    () =>
      selectedSeatIds.filter((seatId) => {
        const seatCfg = ride?.seatMap?.seats?.find((s) => s.id === seatId)
        const row = typeof seatCfg?.row === "number" ? seatCfg.row : seatId.startsWith("front") ? 0 : 1
        return row > 0
      }),
    [selectedSeatIds, ride]
  )

  const seatPrices = useMemo(() => {
    if (!ride?.seatMap?.seats) return {}
    // "opt" (quero conforto) passa pela elegibilidade normal; "confirm" (SIM
    // ao pedido pendente) é elegível POR DEFINIÇÃO — a fileira está justamente
    // em pending porque o diálogo apareceu. R2: o confirmante também paga o
    // prêmio, então o preço EXIBIDO tem que incluí-lo (senão o total do brick
    // diverge do valor que o servidor cobra).
    const opted = selectedSeatIds.filter((sid) =>
      comfortFlags[sid] === "confirm"
        ? true
        : comfortFlags[sid] === "opt" && comfortEligible(ride, sid, pricingCfg, session?.user?.id, isSeatFreeInTrecho)
    )
    return seatPricesForTrecho({
      seats: ride.seatMap.seats,
      ride,
      base: segmentPrice,
      cfg: pricingCfg,
      batchSeatIds: selectedSeatIds,
      isSeatFree: isSeatFreeInTrecho,
      userId: session?.user?.id,
      optInByUser: opted,
    })
  }, [ride, segmentPrice, pricingCfg, selectedSeatIds, isSeatFreeInTrecho, session?.user?.id, comfortFlags])

  // Sem seleção ainda não há "N assentos": mostra o preço cheio do trecho,
  // como sempre foi. A partir do primeiro assento selecionado vale o preço
  // DELE (desconto do meio, prêmio da frente, conforto) — nunca `seg × N`.
  const selectedPrices = useMemo(
    () =>
      selectedSeatIds.length > 0
        ? selectedSeatIds.map((id) => seatPrices[id] ?? segmentPrice)
        : [segmentPrice],
    [selectedSeatIds, seatPrices, segmentPrice]
  )
  const trechoTotalAmount = selectedPrices.reduce((acc, p) => acc + p, 0)
  const reservationFees = selectedPrices.map((p) => getReservationFee(mediation ? p : 0, isAdmin, settings.devMode))
  const totalAmount = reservationFees.reduce((acc, f) => acc + f.total, 0)

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr + "T00:00:00").toLocaleDateString("pt-BR", {
        weekday: "short",
        day: "numeric",
        month: "short",
        // year: "numeric",
      })
    } catch {
      return dateStr
    }
  }

  const handleSeatToggle = (seatId: string) => {
    if (!ride?.seatMap) return
    if (selectedSeatIds.includes(seatId)) {
      setSelectedSeatIds((prev) => prev.filter((id) => id !== seatId))
      setComfortFlags((prev) => {
        const next = { ...prev }
        delete next[seatId]
        return next
      })
      setGenderNotice("")
      return
    }
    // Row real do assento: o seatMap gravado é a fonte de verdade (SUV/VAN têm
    // a fileira 2). Sem isto, todo traseiro caía na fileira 1 e lia a política
    // errada quando o motorista restringiu as fileiras de formas diferentes.
    const seatCfg = ride.seatMap.seats?.find((s) => s.id === seatId)
    const rowNum =
      typeof seatCfg?.row === "number"
        ? seatCfg.row
        : seatId.startsWith("front")
          ? 0
          : seatId.startsWith("row3")
            ? 2
            : 1
    const policy = genderPreferenceForRow(ride.seatMap.genderPolicy, rowNum)
    // Restrição de gênero só age quando o usuário logado tem gênero conhecido.
    // Deslogado pode marcar qualquer assento — a API é quem julga a elegibilidade
    // no momento da reserva e devolve a mensagem pt-BR em caso de recusa.
    if (isLoggedIn && policy && policy !== "INDIFERENTE" && userGender && policy !== userGender) {
      const genderLabel = policy === "FEMININO" ? "feminino" : "masculino"
      setGenderNotice(
        `Esta vaga foi destinada à pessoas do sexo ${genderLabel}.`
      )
      return
    }
    setGenderNotice("")
    // Conforto: a outra janela já optou e está esperando minha decisão. Em vez
    // de reservar, abre o diálogo SIM/NÃO (o backend rejeita compra em espera).
    const cfg = ride?.seatMap?.seatPricing
    if (cfg?.comfortBack === true) {
      const state = seatCfg ? comfortStateFor(ride.seatMap.seats, ride, seatId) : null
      if (
        state &&
        seatCfg &&
        seatCategory(seatCfg) === "back_window" &&
        state.status === "pending" &&
        !state.optedIn.includes(session?.user?.id ?? "")
      ) {
        setComfortPromptSeat(seatId)
        return
      }
    }
    setSelectedSeatIds((prev) => [...prev, seatId])
  }

  const handleComfortDecision = (seatId: string, decision: "confirm" | "decline") => {
    setComfortPromptSeat(null)
    setComfortFlags((prev) => ({ ...prev, [seatId]: decision }))
    setSelectedSeatIds((prev) => (prev.includes(seatId) ? prev : [...prev, seatId]))
  }

  const handleReserve = () => {
    // Permissão de push só faz sentido para quem já tem sessão — pedir a um
    // anônimo desperdiça a única chance de prompt (o browser pode lembrar a
    // recusa) e não há notificação para onde enviar.
    if (isLoggedIn) promptPushPermission()
    setShowPayment(true)
  }

  // Janelas de trás selecionadas que ainda PODEM optar por conforto neste lote
  // — derivações vivem acima com o `seatPricesForTrecho` que as consome.
  const handleComfortToggle = (checked: boolean) => {
    setComfortFlags((prev) => {
      const next = { ...prev }
      for (const sid of comfortEligibleSeats) {
        if (checked) next[sid] = "opt"
        else delete next[sid]
      }
      return next
    })
  }

  // Janela B recusa o conforto: libera o banco do meio deste trecho.
  const handleComfortDecline = async () => {
    if (!ride || !myPendingComfort) return
    setComfortBusy(true)
    setReserveError("")
    try {
      const res = await fetch(
        `/api/rides/${ride._id}/reservations/${myPendingComfort._id}/comfort`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ decision: "decline" }),
        }
      )
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        toast({
          variant: "destructive",
          title: "Não foi possível recusar",
          description: data?.message || "Tente novamente.",
        })
        return
      }
      toast({
        title: "Conforto recusado",
        description: "O banco do meio foi liberado para este trecho.",
      })
      await loadRide()
    } catch {
      toast({
        variant: "destructive",
        title: "Não foi possível recusar",
        description: "Tente novamente.",
      })
    } finally {
      setComfortBusy(false)
    }
  }

  const boardPoint = continuousMode
    ? { lat: queryBoardLat, lng: queryBoardLng, name: searchOriginName }
    : routePoints[boardStopIndex]
  const alightPoint = continuousMode
    ? { lat: queryAlightLat, lng: queryAlightLng, name: searchDestName }
    : routePoints[alightStopIndex]

  const seatPayload = (seatId: string) => ({
    seatId,
    boardStopIndex,
    alightStopIndex,
    boardKm: continuousMode ? queryBoardKm : undefined,
    alightKm: continuousMode ? queryAlightKm : undefined,
    originName: searchOriginName || boardPoint?.name || ride?.origin?.name,
    destinationName: searchDestName || alightPoint?.name || ride?.destination?.name,
    trechoDepartureTime: queryTrechoDepartureTime || null,
    comfortOptIn: comfortFlags[seatId] === "opt",
    comfortConfirm: comfortFlags[seatId] === "confirm",
    comfortDecline: comfortFlags[seatId] === "decline",
    genderAdjacencyGuard: genderGuard && guardEligibleSeats.includes(seatId),
  })

  const monetized = !!settings.platformMonetization

  // `override` permite à retomada pós-login reenviar o lote salvo na intenção
  // sem depender do estado de seleção (que só é restaurado no render seguinte).
  // Retorna a mensagem de erro em pt-BR (ou null em sucesso) para o chamador
  // decidir entre mostrar no diálogo (fluxo logado) ou em toast (retomada).
  const executeDirectReserve = async (override?: {
    seats?: Array<ReturnType<typeof seatPayload>>
    originName?: string
    destinationName?: string
  }): Promise<string | null> => {
    if (!ride) return "Erro ao reservar. Tente novamente."
    const seats = override?.seats ?? selectedSeatIds.map((seatId) => seatPayload(seatId))
    setReserving(true)
    setReserveError("")
    try {
      const res = await fetch("/api/reservations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rideId: ride._id,
          originName: override?.originName || boardPoint?.name || ride.origin.name,
          destinationName: override?.destinationName || alightPoint?.name || ride.destination.name,
          seats,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => null)
        const message = data?.message || "Erro ao reservar. Tente novamente."
        setReserveError(message)
        return message
      }
      router.push("/my-rides")
      return null
    } catch {
      setReserveError("Erro ao reservar. Tente novamente.")
      return "Erro ao reservar. Tente novamente."
    } finally {
      setReserving(false)
    }
  }

  // O efeito de retomada usa este callback, mas ele é recriado a cada render.
  // Guardamos num ref (atualizado num efeito, não no render) para não recriar o
  // efeito de retomada a cada render — padrão anti-cadeia-de-deps do AGENTS.md.
  const executeDirectReserveRef = useRef(executeDirectReserve)
  useEffect(() => {
    executeDirectReserveRef.current = executeDirectReserve
  })

  const handleUnderstand = async () => {
    if (!ride) return
    // Deslogado: a seleção vira uma intenção (token curto) antes do login/
    // cadastro — ela sobrevive a toda a cadeia de autenticação (incluindo o
    // link de confirmação de e-mail aberto em OUTRO aparelho) e a API julga a
    // elegibilidade quando a sessão nascer.
    if (!isLoggedIn) {
      setReserving(true)
      setReserveError("")
      try {
        const seats = selectedSeatIds.map((seatId) => seatPayload(seatId))
        const res = await fetch("/api/reservation-intent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            rideId: ride._id,
            originName: boardPoint?.name || ride.origin.name,
            destinationName: alightPoint?.name || ride.destination.name,
            seats,
            trecho: {
              b: boardStopIndex,
              a: alightStopIndex,
              bk: continuousMode ? queryBoardKm : null,
              ak: continuousMode ? queryAlightKm : null,
            },
            comfortFlags,
            genderGuard,
          }),
        })
        if (!res.ok) {
          const data = await res.json().catch(() => null)
          setReserveError(data?.message || "Não foi possível continuar. Tente novamente.")
          return
        }
        const { token } = await res.json()
        // Preserva TODOS os params do trecho atual (boardStopIndex, kms
        // contínuos/turbo, lat/lng, nomes, preço do segmento) e só acrescenta
        // o token — a página de retorno restaura o contexto exato da seleção.
        const params = new URLSearchParams(searchParams.toString())
        params.set("intentToken", token)
        router.push(`/login?callbackUrl=${encodeURIComponent(`/trip/${ride._id}?${params.toString()}`)}`)
      } catch {
        setReserveError("Não foi possível continuar. Tente novamente.")
      } finally {
        setReserving(false)
      }
      return
    }
    if (monetized) {
      setShowPayment(false)
      setShowPaymentConfirmation(true)
      return
    }
    // Sem monetização a reserva é direta: mantém o diálogo aberto até
    // concluir para exibir erro/spinner em caso de falha.
    await executeDirectReserve()
  }

  const executeReserve = async () => {
    if (!ride) return
    setReserving(true)
    setReserveError("")
    try {
      setShowPaymentConfirmation(false)
      router.push("/my-rides")
    } catch {
      setReserveError("Erro ao reservar. Tente novamente.")
    } finally {
      setReserving(false)
    }
  }

  // Retomada pós-login/cadastro: o deslogado selecionou assentos e foi levado ao
  // login com `?intentToken=...`. Ao voltar autenticado, consumimos a intenção
  // (1x, consumo atômico no servidor) e a API decide o fecho — com monetização
  // abre o pagamento; sem monetização reserva direto com o lote SALVO no
  // servidor (não do estado, que só é restaurado no render seguinte).
  const intentToken = searchParams.get("intentToken")
  useEffect(() => {
    if (!intentToken || intentConsumedRef.current) return
    if (loading || !ride || !isLoggedIn) return
    // `settings` começa `{}` e só ganha `platformMonetization` depois do fetch
    // de /api/settings. Sem esperar por ele, `monetized` seria false e
    // reservaríamos direto numa carona monetizada (pulando o pagamento).
    if (!("platformMonetization" in settings)) return
    intentConsumedRef.current = true

    const stripIntentToken = () => {
      const params = new URLSearchParams(searchParams.toString())
      params.delete("intentToken")
      const qs = params.toString()
      router.replace(`/trip/${id}${qs ? `?${qs}` : ""}`)
    }

    ;(async () => {
      try {
        const res = await fetch("/api/reservation-intent/consume", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: intentToken }),
        })
        if (res.status === 410) {
          stripIntentToken()
          setSelectedSeatIds([])
          setComfortFlags({})
          toast({
            title: "Seleção expirada",
            description: "Sua seleção expirou. Escolha os assentos novamente.",
            variant: "destructive",
          })
          queueMicrotask(() => loadRide())
          return
        }
        if (!res.ok) {
          stripIntentToken()
          toast({
            title: "Não foi possível retomar sua reserva",
            description: "Escolha os assentos novamente.",
            variant: "destructive",
          })
          return
        }
        const intent = await res.json()
        if (
          String(intent?.rideId) !== String(id) ||
          !Array.isArray(intent?.seats) ||
          intent.seats.length === 0
        ) {
          stripIntentToken()
          return
        }
        setSelectedSeatIds(intent.seats.map((s: { seatId: string }) => s.seatId))
        if (intent.trecho && Number.isInteger(intent.trecho.b) && Number.isInteger(intent.trecho.a)) {
          setBoardStopIndex(intent.trecho.b)
          setAlightStopIndex(intent.trecho.a)
        }
        setGenderGuard(!!intent.genderGuard)
        setComfortFlags(intent.comfortFlags && typeof intent.comfortFlags === "object" ? intent.comfortFlags : {})
        stripIntentToken()

        if (monetized) {
          setShowPaymentConfirmation(true)
          return
        }
        const errorMessage = await executeDirectReserveRef.current({
          seats: intent.seats,
          originName: intent.originName,
          destinationName: intent.destinationName,
        })
        if (errorMessage) {
          setSelectedSeatIds([])
          setComfortFlags({})
          toast({
            title: "Não foi possível reservar",
            description: errorMessage,
            variant: "destructive",
          })
          queueMicrotask(() => loadRide())
        }
      } catch {
        toast({
          title: "Não foi possível retomar sua reserva",
          description: "Escolha os assentos novamente.",
          variant: "destructive",
        })
      }
    })()
  }, [intentToken, loading, ride, isLoggedIn, settings, monetized, id, loadRide, toast, router, searchParams])

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6 sm:py-8 max-w-5xl">
        <Card className="bg-white shadow-md overflow-hidden">
          <CardHeader>
            <Skeleton className="h-8 w-3/4" />
            <div className="space-y-2 pt-1">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
            </div>
          </CardHeader>
          <CardContent>
            <Skeleton className="mb-6 h-[300px] w-full rounded-xl" />

            <div className="grid gap-8 md:grid-cols-3">
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
                <Separator className="bg-gray-200" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-40" />
                </div>
                <Separator className="bg-gray-200" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-52" />
                  <Skeleton className="h-4 w-44" />
                  <Skeleton className="h-4 w-36" />
                </div>
                <Separator className="bg-gray-200" />
                <Skeleton className="h-24 w-full rounded-xl" />
              </div>

              <div className="space-y-4">
                <div className="flex justify-center">
                  <Skeleton className="h-64 w-full max-w-[220px] rounded-xl" />
                </div>
                <div className="flex items-center justify-center gap-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="h-12 w-full rounded-lg" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !ride) {
    return (
      <div className="container mx-auto px-4 py-6 sm:py-8 max-w-5xl">
        <Card>
          <CardContent className="p-8 text-center text-blue-500">
            <Image
              src="/informacoes-clean.svg"
              alt="Informações da viagem"
              width={1408}
              height={768}
              unoptimized
              className="mx-auto mb-4 h-auto w-full max-w-[700px]"
            />
            <div>{error || "Carona não encontrada"}</div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const numSeats = Math.max(selectedSeatIds.length, 1)

  const searchOriginCoords = showOriginDisplacement ? { lat: searchOriginLat, lng: searchOriginLng } : undefined
  const searchDestCoords = showDestDisplacement ? { lat: searchDestLat, lng: searchDestLng } : undefined

  // Mesmos horários que o cabeçalho exibe, sem arredondar: a duração sai da
  // diferença entre eles (o "1h 30m" fixo era o placeholder de uma rota de
  // duas horas — e de mentira para trecho de vinte minutos).
  const compactDepartureTime = fromSearch
    ? queryTrechoDepartureTime || formatTime(trechoDeparture) || ride.departureTime
    : ride.departureTime
  const compactArrivalTime = fromSearch
    ? queryTrechoArrivalTime || formatTime(trechoArrival) || ride.estimatedArrivalTime
    : ride.estimatedArrivalTime
  // Nome em duas linhas: cidade/POI em destaque e região abaixo. Passamos as
  // duas partes já resolvidas em vez da string inteira, que quebrava em três
  // linhas no mobile (mantendo o mesmo comportamento do trilho).
  const compactOriginName = fromSearch
    ? searchOriginName || boardPoint?.name || ride.origin.name
    : ride.origin.name
  const compactDestName = fromSearch
    ? searchDestName || alightPoint?.name || ride.destination.name
    : ride.destination.name
  // Com a cidade da busca, a linha 1 é a cidade e a linha 2 o endereço
  // completo ("Terminal…, …, Ubiratã, PR"). Sem cidade, o primeiro segmento
  // do nome (no Nominatim, já é a cidade).
  const compactOriginParts = splitPlaceName(compactOriginName, searchOriginCityName)
  const compactDestParts = splitPlaceName(compactDestName, searchDestCityName)
  const compactPoints: RouteTimelinePoint[] = [
    {
      index: 0,
      name: compactOriginParts.primary,
      secondary: compactOriginParts.secondary,
      time: ceilTimeTo5(compactDepartureTime),
      kind: "origin",
      group: "in",
      alongKm: 0,
    },
    {
      index: alightStopIndex,
      name: compactDestParts.primary,
      secondary: compactDestParts.secondary,
      time: ceilTimeTo5(compactArrivalTime),
      kind: "destination",
      group: "in",
      alongKm: 0,
    },
  ]
  const compactDurationLabel = durationBetweenTimes(
    compactDepartureTime,
    compactArrivalTime,
    ride.route?.duration
  )
  // No trilho completo, o topo mostra sempre a duração do trecho — abrir os
  // grupos não troca o rótulo pela duração da rota inteira.
  const trechoDurationLabel =
    timeline.trechoDurationMinutes != null
      ? formatDurationMinutes(timeline.trechoDurationMinutes)
      : compactDurationLabel

  return (
    <div className="container mx-auto px-4 py-6 sm:py-8 max-w-5xl">
      {ride?.settledAt && !ratingWindowExpired && ratingEligibilityLoaded && (
        <RatingPrompt rideId={ride._id} role={ratingPrompt.role} targets={ratingPrompt.targets} />
      )}
      <p className="text-2xl font-bold text-gray-900 mb-4 px-2 sm:px-4">
        Carona de{" "}
        {splitPlaceName(
          searchOriginName || boardPoint?.name || ride.origin.name,
          searchOriginCityName
        ).primary}{" "}
        para{" "}
        {splitPlaceName(
          searchDestName || alightPoint?.name || ride.destination.name,
          searchDestCityName
        ).primary}
        </p>
      
      <div className="grid gap-8 grid-cols-1 md:grid-cols-3 py-6">
        <div className="md:col-span-2 space-y-6">
          <Card className="bg-white shadow-md overflow-hidden">
            <CardContent>
              <div className="space-y-2">




              <div>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    {availability && (
                      <div className="flex items-center">
                        <Users className="h-5 w-5 mr-2 text-blue-500" />
                        {availability.freeSeats} {availability.freeSeats === 1 ? "assento vago" : "assentos vagos"}
                      </div>
                    )}
                    {/* Fora do gate de vagas: qualquer carona com rota plotável
                      pode abrir o mapa cheio — inclusive a já cheia ou a do
                      próprio motorista. */}
                    {showRouteMapButton && (
                      <div className="ml-auto text-sm text-gray-400">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => setShowRouteMap(true)}
                              className="flex items-center gap-1 hover:text-blue-600 p-4"
                            >
                              <MapIcon className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Ver no mapa</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </li>
                </ul>
              </div>

              {/* Sem o trilho expandido a lista de paradas é a única forma de
                  ver por onde a carona passa — com o trilho, ela é redundante. */}
              {!expandRouteTimeline && ride.stops?.length > 0 && (
                <>
                  <Separator className="bg-gray-200" />
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Paradas</h4>
                    <ul className="list-none text-gray-600">
                      {[...ride.stops]
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                        .map((stop, index) => {
                          // Parada refinada vira POI e perde a cidade do nome;
                          // `cityName` mantém a cidade na linha 1.
                          return (
                            <li key={index} className="flex items-start gap-2 py-0.5">
                              <MapPin className="mt-1 h-4 w-4 shrink-0 text-blue-500" />
                              <PlaceName name={stop.name} cityName={stop.cityName} />
                            </li>
                          )
                        })}
                    </ul>
                  </div>
                </>
              )}












                <p className="text-xl text-gray-800 font-bold mb-2 w-full s">{formatDate(ride.date)}</p>

                {/* Trilho de rota: dois pontos quando a rota é a rota inteira;
                    listagem completa — com as cidades de passagem — quando não é. */}
                <RouteTimeline
                  variant={expandRouteTimeline ? "full" : "compact"}
                  points={expandRouteTimeline ? timeline.points : compactPoints}
                  durationLabel={expandRouteTimeline ? trechoDurationLabel : compactDurationLabel}
                  clockStyle
                />

                <div className="flex items-center justify-between">
                  {/* <div className="flex self-end gap-2">
                    {ride.autoAccept && (
                      <div className="flex items-center gap-2">
                        <Zap className="text-blue-600 w-3" />
                        <p className="hidden md:flex text-xs font-medium text-gray-400">Reserva Automática</p>
                      </div>
                    )}
                    {ride.vehicleId && (
                      <div className="flex items-center gap-4  ml-2">
                        {ride.hasAC && (
                          <div className="flex items-center gap-1" title="Ar condicionado">
                            <Snowflake className="text-blue-600 w-4 h-4" />
                            <span className="hidden md:flex text-xs font-medium text-gray-400">Ar {ride.acZones?.length === 2 ? 'multi-zonas' : 'condicionado'}</span>
                          </div>
                        )}
                        {ride.hasRearDiffusers && (
                          <div className="flex items-center gap-1" title="Difusores traseiros">
                            <AirVent className="text-blue-600 w-4 h-4" />
                            <span className="hidden md:flex text-xs font-medium text-gray-400">Difusores traseiros</span>
                          </div>
                        )}
                        {(ride.hasUSB || ride.hasUSBC) && (
                          <div className="flex items-center gap-1" title="USB">
                            <Usb className="text-blue-600 w-4 h-4" />
                            <span className="hidden md:flex text-xs font-medium text-gray-400">USB</span>
                          </div>
                        )}
                        {ride.hasRearThreePointBelts && (
                          <div className="flex items-center gap-1" title="Cintos de 3 pontos traseiros">
                            <ShieldCheck className="text-blue-600 w-4 h-4" />
                            <span className="hidden md:flex text-xs font-medium text-gray-400">Cintos de 3 pontos traseiros</span>
                          </div>
                        )}
                      </div>
                    )}
                    {isGenderRestricted && (
                      // if female gender restricted
                      isFemaleGenderRestricted ? (
                        <div className="flex items-center gap-1" title="Cintos de 3 pontos traseiros">
                          <User className="text-pink-600 w-4 h-4" />
                          <span className="hidden md:flex text-xs font-medium text-gray-400">Espaço para mulheres</span>
                        </div>
                      )
                        // if male gender restricted
                        : isMaleGenderRestricted ? (
                          <div className="flex items-center gap-1" title="Cintos de 3 pontos traseiros">
                            <User className="text-blue-600 w-4 h-4" />
                            <span className="hidden md:flex text-xs font-medium text-gray-400">Espaço para homens</span>
                          </div>
                        )
                          : null
                    )}
                  </div> */}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white shadow-md overflow-hidden">
            <CardContent className="space-y-4 pt-3">
              <Link href={`/users/${ride.driver?.id}${isLoggedIn && ride.driverId !== session?.user?.id && `?rideId=${ride._id}` || ''}`} className="flex items-center space-x-4 font-semibold text-blue-600 justify-between group hover:bg-blue-50 p-4 px-1 pl-4 -mx-2 rounded-xl transition-colors">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={ride.driver?.avatar || undefined} alt={firstName(ride.driver?.name)} />
                    <AvatarFallback>{ride.driver?.name?.[0] || "?"}</AvatarFallback>
                  </Avatar>
                  <div>
                      {firstName(ride.driver?.name) || "Motorista"}
                    <div className="flex items-center">
                      {hasRatings(driverRating) && <Star className="h-4 w-4 text-yellow-400" />}
                      <span className="ml-1 text-sm text-gray-600">
                        {hasRatings(driverRating) ? `${formatAverage(driverRating)} • ` : ""}
                        {/* Caronas liquidadas, pelo mesmo motivo da nota: a
                            viagem é o denominador. O campo antigo
                            `ride.driver.trips` vinha de `driver.trips`, que
                            ninguém gravava — esta linha mostrava "0 viagens"
                            para todo mundo. */}
                        {driverTrips > 0 ? formatTrips(driverTrips) : "Iniciante"}
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronRightIcon className="h-6 w-6 text-gray-300 group-hover:text-blue-400 transition-colors" />
              </Link>

              <div className="ml-2 mt-2 text-sm space-y-2">
                {ride.vehicle && (
                  <>
                    <div>
                      <p className="text-gray-600 flex items-center">
                        <Car className="h-4 w-4 mr-2 text-gray-500" />
                        {ride.vehicle.brand} {ride.vehicle.model} ({ride.vehicle.color})
                      </p>
                    </div>
                  </>
                )}
                {/* informações do motorista */}
                {/* perfil verificado */}
                {/* numero de celular verificado */}
                {/* documento verificado */}

                {/* comportamento — badge real de taxa de cancelamento.
                    O mockup antigo renderizava as 4 frases ("raramente",
                    "às vezes", ...) ao mesmo tempo, o que era placeholder.
                    Hoje é uma faixa só, calculada do histórico, com a
                    unidade correta (carona para motorista). Base insuficiente
                    não some: mostra "Dados insuficientes" com os contadores,
                    porque ausência de badge pareceria "não cancelou nunca". */}
                <CancelBadgeRow stat={ride.driver?.cancelBadge} />
                {ride.autoAccept ? (
                  <div className="flex items-center gap-2">
                    <Zap className="text-gray-400 w-4 h-4" />
                    <p className="font-medium text-gray-400">Sua reserva será confirmada na hora</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <CalendarClock className="text-gray-600 w-4 h-4" />
                    <p className="font-medium text-gray-400">Sua reserva será confirmada quando o motorista aprovar seu pedido</p>
                  </div>
                )}
                {ride.vehicleId && (
                  <div className="flex items-center gap-4  ml-2">
                    {ride.hasAC && (
                      <div className="flex items-center gap-1" title="Ar condicionado">
                        <Snowflake className="text-gray-400 w-4 h-4" />
                        <span className="font-medium text-gray-400">Ar {ride.acZones?.length === 2 ? 'multi-zonas' : 'condicionado'}</span>
                      </div>
                    )}
                    {ride.hasRearDiffusers && (
                      <div className="flex items-center gap-1" title="Difusores traseiros">
                        <AirVent className="text-gray-400 w-4 h-4" />
                        <span className="font-medium text-gray-400">Difusores traseiros</span>
                      </div>
                    )}
                    {(ride.hasUSB || ride.hasUSBC) && (
                      <div className="flex items-center gap-1" title="USB">
                        <Usb className="text-gray-400 w-4 h-4" />
                        <span className="font-medium text-gray-400">USB</span>
                      </div>
                    )}
                    {ride.hasRearThreePointBelts && (
                      <div className="flex items-center gap-1" title="Cintos de 3 pontos traseiros">
                        <ShieldCheck className="text-gray-400 w-4 h-4" />
                        <span className="font-medium text-gray-400">Cintos de 3 pontos traseiros</span>
                      </div>
                    )}
                  </div>
                )}
                {(() => {
                  const g = ride?.seatMap?.genderPolicy as any
                  let hf = false, hm = false
                  if (g && typeof g === "object") {
                    for (const v of Object.values(g)) {
                      if (v === "FEMININO") hf = true
                      if (v === "MASCULINO") hm = true
                    }
                  }
                  return (hf || hm) ? (
                    <div className="flex items-center gap-1" title="Política de gênero">
                      {hf && (
                        <div className="flex items-center gap-1">
                          <User className="text-pink-600 w-4 h-4" />
                          <span className="font-medium text-gray-400">Espaço para mulheres</span>
                        </div>
                      )}
                      {hm && (
                        <div className="flex items-center gap-1">
                          <User className="text-blue-600 w-4 h-4" />
                          <span className="font-medium text-gray-400">Espaço para homens</span>
                        </div>
                      )}
                    </div>
                  ) : null
                })()}
              </div>
              {ride.description && (
                <p className="text-sm text-gray-600 border-b border-gray-200 pb-4 whitespace-pre-line">
                  {/* {ride.description.replace(/\\n/g, '\n')} */}
                  {ride.description}
                </p>
              )}

              {/* <div className="space-y-4">
                {ride.description && (
                  <>
                    <Separator className="bg-gray-200" />
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2">Descrição</h4>
                      <p className="text-gray-600 text-sm">
                        {ride.description.split('\n').map((line, index) => (
                          <span key={index}>
                            {line}
                            {index < ride.description.split('\n').length - 1 && <br />}
                          </span>
                        ))}
                      </p>
                    </div>
                  </>
                )}
              </div> */}
              
              {/* se for passageiro */}
              {isLoggedIn && ride.driverId !== session?.user?.id && (
                <div className="flex items-center gap-2 justify-between">
                  <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 w-full md:w-auto" asChild>
                    <Link href={outsiderChatHref}>
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Falar com {ride.driver?.name?.split(" ")[0] || "Motorista"}
                    </Link>
                  </Button>
                  {isLoggedIn && ride.driverId !== session?.user?.id && (
                    <div className="flex items-center gap-2 justify-between">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                            <Flag className="h-4 w-4" />
                            <span className="hidden md:flex md:ml-2">
                              Denunciar
                            </span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link href={`/report/${ride._id}?targetType=ride`}>Denunciar carona</Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link href={`/report/${ride.driverId}?targetType=user`}>Denunciar motorista</Link>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="bg-white shadow-md overflow-hidden">
            <CardContent>
              <div className="space-y-4">
                {departed && (
                  <p className="text-md text-gray-600 text-center">
                    Esta carona já partiu.
                  </p>
                )}
                {ride.seatMap && departed !== true && (
                  <>
                    <SeatMap
                      vehicleType={ride.seatMap.vehicleType}
                      seats={displaySeats}
                      mode={isDriver ? "view_only" : "passenger_multi_select"}
                      driverGenderPolicy={ride.seatMap.genderPolicy}
                      currentPassengerGender={userGender}
                      selectedSeatIds={isDriver ? undefined : selectedSeatIds}
                      onSeatSelect={isDriver ? undefined : handleSeatToggle}
                      seatPrices={seatPrices}
                      seatPassengers={seatPassengers}
                    />
                    {genderNotice && (
                      <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                        {genderNotice}
                      </p>
                    )}
                    {rowCapNoticeText && (
                      <p className="mt-2 flex items-start gap-2 text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-md p-2">
                        <Users className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                        <span>{rowCapNoticeText}</span>
                      </p>
                    )}
                    {comfortEligibleSeats.length > 0 && (
                      <label
                        htmlFor="comfort-optin"
                        className="flex items-start gap-3 mt-3 border border-blue-200 bg-blue-50/60 rounded-lg p-3 cursor-pointer"
                      >
                        <Checkbox
                          id="comfort-optin"
                          checked={optedNow.length === comfortEligibleSeats.length}
                          onCheckedChange={(v) => handleComfortToggle(v === true)}
                          className="mt-0.5"
                        />
                        <span className="text-sm">
                          Quero conforto de sobra
                          <span className="block text-xs text-muted-foreground">
                            O banco do meio fica livre neste trecho. Adicional de R$ {comfortSurge.toFixed(0)} por janela
                            {comfortEligibleSeats.length > 1
                              ? ` — aplicado às ${comfortEligibleSeats.length} janelas selecionadas`
                              : ""}.
                          </span>
                        </span>
                      </label>
                    )}
                    {guardEligibleSeats.length > 0 && userGender && (
                      <label
                        htmlFor="gender-adjacency-guard"
                        className="flex items-start gap-3 mt-3 border border-gray-200 bg-gray-50/60 rounded-lg p-3 cursor-pointer"
                      >
                        <Checkbox
                          id="gender-adjacency-guard"
                          checked={genderGuard}
                          onCheckedChange={(v) => setGenderGuard(v === true)}
                          className="mt-0.5"
                        />
                        <span className="text-sm">
                          Viajar apenas ao lado de pessoas do mesmo gênero que o meu
                          <span className="block text-xs text-muted-foreground">
                            Se uma pessoa de outro gênero reservar o assento ao lado neste trecho,
                            sua reserva será cancelada e reembolsada automaticamente.
                          </span>
                        </span>
                      </label>
                    )}
                    {myPendingComfort && (
                      <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/60 p-3">
                        <p className="text-sm font-medium text-gray-900">
                          Pedido de conforto traseiro
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Uma pessoa da sua fileira quer viajar com o banco do meio livre.
                          Aceite pagando R$ {comfortDueAmount.toFixed(0)} ou recuse para liberar o meio.
                        </p>
                        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                          <Button
                            type="button"
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            disabled={comfortBusy}
                            onClick={() => {
                              setReserveError("")
                              setShowComfortPayment(true)
                            }}
                          >
                            Aceitar e pagar R$ {comfortDueAmount.toFixed(0)}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full border-gray-300 text-gray-600 hover:bg-gray-100"
                            disabled={comfortBusy}
                            onClick={handleComfortDecline}
                          >
                            {comfortBusy ? "Recusando..." : "Recusar"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {ride.driverId !== session?.user?.id && departed !== true && (
                  <>
                    <div className="text-center border-t border-gray-200 pt-4">
                      {(() => {
                        const n = Math.max(numSeats, 1)
                        const trechoTotal = trechoTotalAmount
                        if (!monetized) {
                          return <p className="text-4xl font-bold text-blue-600">R$ {trechoTotal.toFixed(0)}</p>
                        }
                        const feeRes = getReservationFee(mediation ? segmentPrice : 0, isAdmin, settings.devMode)
                        const feePerSeat = feeRes.total - (mediation ? segmentPrice : 0)
                        const feeAmount = feePerSeat * n
                        return <>
                          <p className="text-4xl font-bold text-blue-600">R$ {trechoTotal.toFixed(0)}</p>
                          <p className="text-sm text-muted-foreground">+ R$ {feeAmount.toFixed(2)} taxa de reserva</p>
                          {/* <p className="text-sm text-gray-600 mt-2 font-semibold">
                            Total: R$ {total % 1 === 0 ? total.toFixed(0) : total.toFixed(2)}
                          </p> */}
                        </>
                      })()}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4 text-center">

            {isDriver ? null : (
              <Button
                onClick={handleReserve}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={
                  departed ||
                  (availability?.freeSeats || 0) === 0 ||
                  (ride.seatMap && selectedSeatIds.length === 0)
                }
              >
                {departed
                  ? "Esta carona já partiu"
                  : (availability?.freeSeats || 0) === 0
                    ? "Cheio"
                    : selectedSeatIds.length === 0
                      ? "Selecione um assento"
                      : "Reservar"}
              </Button>
            )}

            {isLoggedIn && ride.driverId === session?.user?.id && !isRideFinished(ride) && (
              <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" className="border-gray-300 text-gray-600 hover:bg-gray-100 col-span-2" asChild>
                    <Link href={`/manage-ride/${ride._id}`}>
                      <Settings className="h-4 w-4 mr-2" />
                      Gerenciar carona
                    </Link>
                  </Button>
              </div>
            )}
          </div>

        </div>

      </div>

      {passengersInSelectedSegment.length > 0 && (
      <div className="grid gap-8 md:grid-cols-3 pb-6">
        <div className="col-span-2 space-y-6">
        <Card className="bg-white shadow-md overflow-hidden">
          <CardContent>

            {/* <div className="grid gap-8 md:grid-cols-3"> */}
              <div className="space-y-4">

                {/* <Separator className="bg-gray-200" /> */}
                <div>
                  <h4 className="text-xl font-semibold text-gray-700 mb-4">Passageiros</h4>
                  <div className="space-y-2">
                    {groupedPassengersToShow.map((passenger) => {
                      const reservation = (ride.reservations || []).find(
                        (r: any) => String(r.passengerId) === String(passenger.passengerId)
                      ) as any
                      const seg =
                        reservation && Number.isInteger(reservation.boardStopIndex) && Number.isInteger(reservation.alightStopIndex)
                          ? `b${reservation.boardStopIndex}:a${reservation.alightStopIndex}`
                          : reservation && typeof reservation.boardKm === "number" && typeof reservation.alightKm === "number"
                            ? `b${Math.round(reservation.boardKm)}:a${Math.round(reservation.alightKm)}`
                            : "b0:a1"
                      const driverChatHref = reservation
                        ? `/chat/${ride._id}/${seg}?target=${passenger.passengerId}&boardStopIndex=${reservation.boardStopIndex ?? ""}&alightStopIndex=${reservation.alightStopIndex ?? ""}&boardKm=${reservation.boardKm ?? ""}&alightKm=${reservation.alightKm ?? ""}`
                        : `/chat/${ride._id}/${seg}?target=${passenger.passengerId}`
                      return (
                        <div key={passenger.passengerId} className="flex items-center justify-between gap-2">
                          <Link
                            href={`/users/${passenger.passengerId}${isDriver ? `?rideId=${ride._id}` : ""}`}
                            className="flex items-center space-x-2 text-gray-600 min-w-0"
                          >
                            <Avatar className="h-8 w-8 shrink-0">
                              <AvatarImage src={passenger.avatar || undefined} alt={firstName(passenger.name)} />
                              <AvatarFallback>{passenger.name?.[0] || "P"}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="truncate">
                                {firstName(passenger.name)}
                                {passenger.count > 1 && (
                                  <span className="text-sm text-gray-400"> +{passenger.count - 1}</span>
                                )}
                              </p>
                              {passenger.trechos.length > 0 && (
                                <p className="text-xs text-gray-500 truncate">{passenger.trechos.join("; ")}</p>
                              )}
                            </div>
                          </Link>
                          {isDriver && (
                            <Button variant="outline" size="sm" asChild className="shrink-0">
                              <Link href={driverChatHref}>
                                <MessageCircle className="h-4 w-4" />
                                <span className="hidden sm:flex text-sm sm:ml-1">Mensagem</span>
                              </Link>
                            </Button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
                {/* Avaliar movido para o topo da página quando settled + janela aberta */}
             </div>

            {/* </div> */}
          </CardContent>
        </Card>
        </div>
      </div>
      )}

      <Dialog open={showPayment} onOpenChange={(open) => { if (!reserving) setShowPayment(open) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Avisos importantes</DialogTitle>
            <DialogDescription>
              Antes de prosseguir com a reserva, por favor, leia os seguintes avisos:
            </DialogDescription>
          </DialogHeader>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Converse com o motorista sobre o espaço para bagagem</li>
            <li>Combine com o motorista o ponto de encontro para embarque e o local de desembarque</li>
            <li>
              Confirme o seu biotipo e dos demais passageiros para garantir uma viagem tranquila e com maior conforto
            </li>
          </ul>
          {reserveError && (
            <p className="text-sm text-destructive">{reserveError}</p>
          )}
          <Button
            onClick={handleUnderstand}
            disabled={reserving}
            className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white"
          >
            {reserving ? "Reservando..." : "Entendi"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentConfirmation} onOpenChange={setShowPaymentConfirmation}>
          <DialogContent
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle>Reservar viagem</DialogTitle>
              <DialogDescription>Complete o pagamento para confirmar {selectedSeatIds.length > 1 ? `suas ${selectedSeatIds.length} reservas` : "sua reserva"}</DialogDescription>
            </DialogHeader>
            <div className="text-center mb-4">
              {(() => {
                const fee = reservationFees[0]
                const samePrice = selectedPrices.every((p) => p === selectedPrices[0])
                return <>
                  <p className="text-2xl font-bold text-blue-600">R$ {totalAmount.toFixed(0)}</p>
                  {numSeats > 1 && samePrice && (
                    <p className="text-sm text-gray-500">{numSeats} assentos x R$ {selectedPrices[0].toFixed(0)}</p>
                  )}
                  {numSeats > 1 && !samePrice && (
                    <p className="text-sm text-gray-500">
                      {numSeats} assentos: {selectedPrices.map((p) => `R$ ${p.toFixed(0)}`).join(" + ")}
                    </p>
                  )}
                  {numSeats > 1 && fee.breakdown && (
                    <p className="text-sm text-muted-foreground">{fee.breakdown} x {numSeats} assentos</p>
                  )}
                  {numSeats === 1 && fee.breakdown && <p className="text-sm text-muted-foreground">{fee.breakdown}</p>}
                </>
              })()}
            </div>
            {reserveError && (
              <p className="text-sm text-destructive text-center mb-2">{reserveError}</p>
            )}
            <MercadoPagoBricks
              amount={totalAmount}
              description={`Reserva ${boardPoint?.name || "Origem"} → ${alightPoint?.name || "Destino"} • Viagem de ${ride.origin.name} para ${ride.destination.name}`}
              externalReference={`reservation:${ride._id}`}
              extra={{
                type: "reservation",
                rideId: ride._id,
                seats: selectedSeatIds.map((seatId) => seatPayload(seatId)),
                originName: boardPoint?.name || ride.origin.name,
                destinationName: alightPoint?.name || ride.destination.name,
              }}
              onSuccess={() => executeReserve()}
              onError={(err) => setReserveError(err)}
            />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!comfortPromptSeat}
        onOpenChange={(open) => {
          if (!open) setComfortPromptSeat(null)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Conforto traseiro</DialogTitle>
            <DialogDescription>
              Uma janela desta fileira já optou por viajar com o banco do meio
              livre. Você acompanha essa escolha para o seu assento? O adicional de R$ {comfortSurge.toFixed(0)} por janela será aplicado caso confirme.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => comfortPromptSeat && handleComfortDecision(comfortPromptSeat, "confirm")}
            >
              SIM
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full border-gray-300 text-gray-600 hover:bg-gray-100"
              onClick={() => comfortPromptSeat && handleComfortDecision(comfortPromptSeat, "decline")}
            >
              NÃO
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showComfortPayment && !!myPendingComfort}
        onOpenChange={(open) => {
          if (!comfortBusy) setShowComfortPayment(open)
        }}
      >
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Confirmar conforto traseiro</DialogTitle>
            <DialogDescription>
              Pague o adicional para viajar com o banco do meio livre nesta fileira.
            </DialogDescription>
          </DialogHeader>
          {ride && myPendingComfort && (
            <>
              <div className="mb-4 text-center">
                <p className="text-2xl font-bold text-blue-600">R$ {comfortDueAmount.toFixed(0)}</p>
                <p className="text-sm text-muted-foreground">Adicional de conforto (janela)</p>
              </div>
              {reserveError && (
                <p className="mb-2 text-center text-sm text-destructive">{reserveError}</p>
              )}
              <MercadoPagoBricks
                amount={comfortDueAmount}
                description={`Conforto traseiro • ${ride.origin.name} → ${ride.destination.name}`}
                externalReference={`comfort_confirm:${ride._id}`}
                extra={{
                  type: "comfort_confirm",
                  rideId: ride._id,
                  reservationId: myPendingComfort._id,
                }}
                onSuccess={() => {
                  setShowComfortPayment(false)
                  toast({
                    title: "Conforto confirmado",
                    description: "O banco do meio ficará livre para a sua fileira.",
                  })
                  loadRide()
                }}
                onError={(err) => setReserveError(err)}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Mapa cheio da rota ("Ver no mapa"). Com trecho selecionado na URL
          (busca/turbo) o traçado azul + pins de embarque/desembarque entram
          por cima; na URL da carona pura, só a rota com origem/destino/paradas. */}
      {ride && (
        <RouteMapDialog
          open={showRouteMap}
          onOpenChange={setShowRouteMap}
          origin={{ name: ride.origin.name, lat: ride.origin.lat, lng: ride.origin.lng }}
          destination={{ name: ride.destination.name, lat: ride.destination.lat, lng: ride.destination.lng }}
          stops={ride.stops?.map((s) => ({ name: s.name, lat: s.lat, lng: s.lng, price: s.price }))}
          overviewPolyline={ride.route?.overviewPolyline}
          passengerSegment={
            trechoPoints.length >= 2 ? { points: trechoPoints, color: "#1d4ed8" } : undefined
          }
          boardPoint={
            trechoSelected &&
            boardPoint &&
            Number.isFinite(boardPoint.lat) &&
            Number.isFinite(boardPoint.lng)
              ? { lat: boardPoint.lat, lng: boardPoint.lng, name: boardPoint.name }
              : undefined
          }
          alightPoint={
            trechoSelected &&
            alightPoint &&
            Number.isFinite(alightPoint.lat) &&
            Number.isFinite(alightPoint.lng)
              ? { lat: alightPoint.lat, lng: alightPoint.lng, name: alightPoint.name }
              : undefined
          }
          showOriginDisplacement={showOriginDisplacement}
          showDestDisplacement={showDestDisplacement}
          searchOriginCoords={searchOriginCoords}
          searchDestCoords={searchDestCoords}
          dimStopIndexes={dimStopIndexes}
          reservationPoints={reservationPoints}
        />
      )}
    </div>
  )
}
